<div class="modal-header bg-danger text-white">
  <h5 class="modal-title">
    <i class="fa fa-exclamation-triangle me-2"></i>
    {{ 'manageEvent.cancelModal.title' | translate }}
  </h5>
  <button type="button" class="btn-close btn-close-white" aria-label="Close" (click)="dismiss()"
          [disabled]="processing"></button>
</div>

<div class="modal-body">
  <!-- Step 1: Warning -->
  @if (currentStep === 'warning') {
    <div class="cancel-step">
      <div class="alert alert-danger mb-4">
        <i class="fa fa-exclamation-circle me-2"></i>
        <strong>{{ 'manageEvent.cancelModal.warningTitle' | translate }}</strong>
        <p class="mb-0 mt-2">{{ 'manageEvent.cancelModal.warningDescription' | translate }}</p>
      </div>

      <div class="event-info mb-4">
        <h6>{{ 'manageEvent.cancelModal.eventInfo' | translate }}</h6>
        <div class="card bg-light">
          <div class="card-body">
            <p class="mb-1"><strong>{{ event?.event_name }}</strong></p>
            <p class="mb-0 text-muted small">
              {{ event?.start_date | date:'mediumDate' }} - {{ event?.end_date | date:'mediumDate' }}
            </p>
          </div>
        </div>
      </div>

      @if (paidOrdersCount > 0) {
        <div class="refund-summary mb-4">
          <h6>{{ 'manageEvent.cancelModal.refundSummary' | translate }}</h6>
          <div class="card border-warning">
            <div class="card-body">
              <div class="row">
                <div class="col-6">
                  <p class="mb-0 text-muted">{{ 'manageEvent.cancelModal.ordersToRefund' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold">{{ paidOrdersCount }}</p>
                </div>
                <div class="col-6">
                  <p class="mb-0 text-muted">{{ 'manageEvent.cancelModal.totalToRefund' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold">{{ formatAmount(totalOrderAmount) }} NOK</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <div class="consequences mb-4">
        <h6>{{ 'manageEvent.cancelModal.consequences' | translate }}</h6>
        <ul class="list-unstyled">
          <li class="mb-2">
            <i class="fa fa-times-circle text-danger me-2"></i>
            {{ 'manageEvent.cancelModal.consequence1' | translate }}
          </li>
          <li class="mb-2">
            <i class="fa fa-credit-card text-warning me-2"></i>
            {{ 'manageEvent.cancelModal.consequence2' | translate }}
          </li>
          <li class="mb-2">
            <i class="fa fa-hand-holding-dollar text-info me-2"></i>
            {{ 'manageEvent.cancelModal.consequence3' | translate }}
          </li>
          <li class="mb-2">
            <i class="fa fa-eye text-secondary me-2"></i>
            {{ 'manageEvent.cancelModal.consequence5' | translate }}
          </li>
        </ul>
      </div>
    </div>
  }

  <!-- Step 2: Confirmation -->
  @if (currentStep === 'confirm') {
    <div class="cancel-step">
      <div class="alert alert-danger mb-4">
        <i class="fa fa-exclamation-triangle me-2"></i>
        {{ 'manageEvent.cancelModal.confirmTitle' | translate }}
      </div>

      <div class="confirmation-input mb-4">
        <label class="form-label">
          {{ 'manageEvent.cancelModal.confirmDescription' | translate }}
        </label>
        <input type="text"
               class="form-control form-control-lg"
               [(ngModel)]="confirmationText"
               [placeholder]="'manageEvent.cancelModal.confirmPlaceholder' | translate"
               autocomplete="off">
      </div>

      @if (confirmationText && !confirmationMatches) {
        <div class="alert alert-warning">
          <i class="fa fa-info-circle me-2"></i>
          {{ 'manageEvent.cancelModal.confirmDescription' | translate }}
        </div>
      }
    </div>
  }

  <!-- Step 3: Processing -->
  @if (currentStep === 'processing') {
    <div class="cancel-step text-center py-5">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <h5>{{ 'manageEvent.cancelModal.processingTitle' | translate }}</h5>
      <p class="text-muted">{{ 'manageEvent.cancelModal.processingDescription' | translate }}</p>
    </div>
  }

  <!-- Step 4: Result -->
  @if (currentStep === 'result') {
    <div class="cancel-step">
      @if (error) {
        <div class="alert alert-danger mb-4">
          <i class="fa fa-times-circle me-2"></i>
          {{ error }}
        </div>
      } @else if (cancelResult) {
        <div class="alert alert-success mb-4">
          <i class="fa fa-check-circle me-2"></i>
          {{ 'manageEvent.cancelModal.resultSuccessTitle' | translate }}
        </div>

        <div class="result-summary">
          <p class="text-muted mb-4">{{ 'manageEvent.cancelModal.resultDescription' | translate }}</p>

          <div class="row g-3 mb-4">
            <div class="col-6 col-md-3">
              <div class="card bg-light text-center">
                <div class="card-body py-3">
                  <p class="mb-0 text-muted small">{{ 'common.total' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold">{{ cancelResult.total_orders }}</p>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card bg-success bg-opacity-10 text-center">
                <div class="card-body py-3">
                  <p class="mb-0 text-muted small">{{ 'manageEvent.cancelModal.ordersRefunded' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold text-success">{{ cancelResult.successful_refunds }}</p>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card bg-danger bg-opacity-10 text-center">
                <div class="card-body py-3">
                  <p class="mb-0 text-muted small">{{ 'manageEvent.cancelModal.failedRefunds' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold text-danger">{{ cancelResult.failed_refunds }}</p>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="card bg-warning bg-opacity-10 text-center">
                <div class="card-body py-3">
                  <p class="mb-0 text-muted small">{{ 'manageEvent.cancelModal.manualRefundsRequired' | translate }}</p>
                  <p class="mb-0 fs-4 fw-bold text-warning">{{ cancelResult.manual_refunds_required }}</p>
                </div>
              </div>
            </div>
          </div>

          @if (cancelResult.manual_refunds_required > 0) {
            <div class="alert alert-warning">
              <i class="fa fa-hand-holding-dollar me-2"></i>
              {{ 'manageEvent.cancelModal.manualRefundsNote' | translate }}
            </div>
          }

          @if (cancelResult.refund_errors && cancelResult.refund_errors.length > 0) {
            <div class="refund-errors mt-4">
              <h6 class="text-danger">{{ 'common.errors' | translate }}</h6>
              <ul class="list-group">
                @for (err of cancelResult.refund_errors; track err.order_id) {
                  <li class="list-group-item list-group-item-danger">
                    <strong>{{ 'manageEvent.cancelModal.order' | translate }} #{{ err.order_id }}:</strong>
                    {{ getCleanErrorMessage(err.error) }}
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      }
    </div>
  }
</div>

<div class="modal-footer">
  @if (currentStep === 'warning') {
    <app-button variant="neutral" [label]="'manageEvent.cancelModal.cancel' | translate" (clicked)="dismiss()"></app-button>
    <app-button variant="danger" [label]="'manageEvent.cancelModal.continue' | translate" (clicked)="nextStep()"></app-button>
  }

  @if (currentStep === 'confirm') {
    <app-button variant="neutral" [label]="'manageEvent.cancelModal.goBack' | translate" (clicked)="previousStep()"></app-button>
    <app-button variant="danger"
                [label]="'manageEvent.cancelModal.confirmCancel' | translate"
                [disabled]="!confirmationMatches"
                (clicked)="nextStep()"></app-button>
  }

  @if (currentStep === 'processing') {
    <!-- No buttons during processing -->
  }

  @if (currentStep === 'result') {
    <app-button variant="primary"
                [label]="'manageEvent.cancelModal.close' | translate"
                (clicked)="close(cancelResult !== null)"></app-button>
  }
</div>
